import type { Message, ThreadParticipant } from "./core/types.js";
// A thread's reply count counts people's and agents' messages, never a system row (a
// work-session notice, a hop-limit line) nor an agent's own status line (source
// "agent_status"). Same rule as the server (thread-reply-rows.ts).
interface ReplyRow {
  fromType?: string | null;
  source?: string | null;
}

export function countsAsThreadReply(msg: ReplyRow): boolean {
  return msg.fromType !== "system" && msg.source !== "agent_status";
}

// The thread pane header's "N replies": the same rule as the channel row's count.
export function threadReplyCount(replies: readonly ReplyRow[]): number {
  return replies.filter(countsAsThreadReply).length;
}

// The relay's reload keeps the first 4 distinct repliers (pg-sync getThreadParticipantsForRoots).
const FACEPILE_CAP = 4;

export type LiveReply = Pick<
  Message,
  "id" | "fromId" | "fromType" | "fromName" | "cyboId" | "sessionAgentId" | "source" | "createdAt"
>;

// The workspace roster (session agentId → cybo) and the viewer's cybos.
export type ReplyAgents = ReadonlyArray<{ agentId: string; cyboId?: string | null }>;
export type ReplyCybos = ReadonlyArray<{ id: string }>;

// The cybo an agent reply belongs to, as the reload resolves it: a bound session's frame
// carries fromId = its cybo (sessionAgentId names the session); else the roster or the cybo list.
function replyCyboId(
  reply: LiveReply,
  agents?: ReplyAgents,
  cybos?: ReplyCybos,
): string | undefined {
  if (reply.cyboId) return reply.cyboId;
  if (reply.sessionAgentId && reply.sessionAgentId !== reply.fromId) return reply.fromId;
  const rosterCybo = agents?.find((a) => a.agentId === reply.fromId)?.cyboId;
  if (rosterCybo) return rosterCybo;
  return cybos?.some((c) => c.id === reply.fromId) ? reply.fromId : undefined;
}

// A root after one live reply: count, last reply time and facepile move together, in the
// reload's shape. An agent joins as its cybo with no image (faces resolve by id, so a frame
// never puts an emoji over a photo); one with no cybo is skipped, as the reload drops it.
export function rootWithReply(
  root: Message,
  reply: LiveReply,
  agents?: ReplyAgents,
  cybos?: ReplyCybos,
): Message {
  const isAgent = reply.fromType === "agent";
  const id = isAgent ? replyCyboId(reply, agents, cybos) : reply.fromId;
  const list = root.threadParticipants ?? [];
  const joins = id !== undefined && list.length < FACEPILE_CAP && !list.some((p) => p.id === id);
  const kind: ThreadParticipant["kind"] = isAgent ? "agent" : "human";
  return {
    ...root,
    replyCount: (root.replyCount ?? 0) + 1,
    lastReplyAt: reply.createdAt,
    threadParticipants: joins
      ? [...list, { id, kind, name: reply.fromName ?? "", image: null }]
      : root.threadParticipants,
  };
}
